import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useAuth } from "../../../contexts/AuthContext";
import { getRoleDefaultRoute } from "../../../utils/roleRedirect";

const loginSchema = z.object({
  email: z.string().email("Email inválido"),
  password: z.string().min(6, "La contraseña debe tener al menos 6 caracteres"),
});

type LoginFormData = z.infer<typeof loginSchema>;

// Componente de animación de partículas
const ParticleAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Configurar canvas
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Configuración de partículas
    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
    }> = [];

    // Crear partículas
    const createParticles = () => {
      for (let i = 0; i < 50; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 1,
          opacity: Math.random() * 0.5 + 0.1,
        });
      }
    };

    // Dibujar partículas
    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Dibujar gradiente de fondo
      const gradient = ctx.createLinearGradient(
        0,
        0,
        canvas.width,
        canvas.height
      );
      gradient.addColorStop(0, "rgba(59, 130, 246, 0.1)");
      gradient.addColorStop(1, "rgba(147, 51, 234, 0.1)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Actualizar y dibujar partículas
      particles.forEach((particle, index) => {
        // Actualizar posición
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Rebotar en los bordes
        if (particle.x <= 0 || particle.x >= canvas.width) particle.vx *= -1;
        if (particle.y <= 0 || particle.y >= canvas.height) particle.vy *= -1;

        // Dibujar partícula
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(59, 130, 246, ${particle.opacity})`;
        ctx.fill();

        // Conectar partículas cercanas
        particles.forEach((otherParticle, otherIndex) => {
          if (index !== otherIndex) {
            const dx = particle.x - otherParticle.x;
            const dy = particle.y - otherParticle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 100) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(otherParticle.x, otherParticle.y);
              ctx.strokeStyle = `rgba(59, 130, 246, ${
                0.1 * (1 - distance / 100)
              })`;
              ctx.lineWidth = 1;
              ctx.stroke();
            }
          }
        });
      });

      requestAnimationFrame(drawParticles);
    };

    createParticles();
    drawParticles();

    return () => {
      window.removeEventListener("resize", resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: -1 }}
    />
  );
};

export const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      const result = await login(data);

      if (result.status === "success" && result.data?.usuario) {
        const user = result.data.usuario;
        const redirectRoute = getRoleDefaultRoute(user.rol);

        // Usar window.location para forzar recarga completa y asegurar que el estado se actualice
        window.location.href = redirectRoute;
      } else {
        setError("root", {
          type: "manual",
          message: "No se encontró información del usuario en el resultado",
        });
      }
    } catch (error: any) {
      setError("root", {
        type: "manual",
        message: error.message || "Error en el login",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animación de partículas */}
      <ParticleAnimation />

      {/* Líneas decorativas animadas */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Círculos principales distribuidos por toda la pantalla */}
        <div
          className="absolute top-1/4 left-1/4 w-64 h-64 border-4 border-blue-400 rounded-full animate-bounce opacity-60"
          style={{ animationDuration: "3s" }}
        ></div>
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 border-4 border-purple-400 rounded-full animate-bounce opacity-60"
          style={{ animationDelay: "1s", animationDuration: "4s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-4 border-indigo-400 rounded-full animate-bounce opacity-70"
          style={{ animationDelay: "2s", animationDuration: "2.5s" }}
        ></div>

        {/* Círculos adicionales redistribuidos - movidos desde esquina superior izquierda */}
        <div
          className="absolute top-1/3 right-1/3 w-48 h-48 border-2 border-blue-300 rounded-full animate-bounce opacity-50"
          style={{ animationDelay: "0.5s", animationDuration: "3.5s" }}
        ></div>
        <div
          className="absolute bottom-1/3 left-1/3 w-40 h-40 border-2 border-purple-300 rounded-full animate-bounce opacity-50"
          style={{ animationDelay: "1.5s", animationDuration: "4.5s" }}
        ></div>

        {/* Figuras geométricas redistribuidas - movidas desde esquina superior izquierda */}
        <div
          className="absolute top-1/6 right-1/4 w-24 h-24 border-2 border-pink-400 rounded-full animate-bounce opacity-60"
          style={{ animationDelay: "0.3s", animationDuration: "2s" }}
        ></div>
        <div
          className="absolute bottom-1/6 left-1/6 w-32 h-32 border-2 border-cyan-400 rounded-full animate-bounce opacity-60"
          style={{ animationDelay: "0.8s", animationDuration: "3s" }}
        ></div>

        {/* Triángulos redistribuidos - movidos desde esquina superior izquierda */}
        <div
          className="absolute top-1/5 right-1/4 w-0 h-0 border-l-[30px] border-l-transparent border-b-[52px] border-b-pink-300 border-r-[30px] border-r-transparent animate-bounce opacity-50"
          style={{ animationDelay: "1.2s", animationDuration: "2.8s" }}
        ></div>
        <div
          className="absolute bottom-1/5 left-1/5 w-0 h-0 border-l-[25px] border-l-transparent border-t-[43px] border-t-cyan-300 border-r-[25px] border-r-transparent animate-bounce opacity-50"
          style={{ animationDelay: "0.7s", animationDuration: "3.2s" }}
        ></div>

        {/* Cuadrados rotados redistribuidos - movidos desde esquina superior izquierda */}
        <div
          className="absolute top-1/4 right-1/3 w-20 h-20 border-2 border-yellow-400 transform rotate-45 animate-bounce opacity-50"
          style={{ animationDelay: "1.8s", animationDuration: "3.8s" }}
        ></div>
        <div
          className="absolute bottom-1/4 left-1/3 w-16 h-16 border-2 border-green-400 transform rotate-45 animate-bounce opacity-50"
          style={{ animationDelay: "0.4s", animationDuration: "2.3s" }}
        ></div>

        {/* Círculos pequeños redistribuidos - movidos desde esquina superior izquierda */}
        <div
          className="absolute top-1/8 right-1/3 w-12 h-12 border-2 border-orange-400 rounded-full animate-bounce opacity-70"
          style={{ animationDelay: "2.5s", animationDuration: "2.7s" }}
        ></div>
        <div
          className="absolute bottom-1/8 left-1/8 w-14 h-14 border-2 border-teal-400 rounded-full animate-bounce opacity-70"
          style={{ animationDelay: "1.7s", animationDuration: "3.3s" }}
        ></div>

        {/* Líneas diagonales redistribuidas - movidas desde esquina superior izquierda */}
        <div
          className="absolute top-1/3 right-1/4 w-32 h-0.5 bg-gradient-to-r from-transparent via-pink-400 to-transparent transform rotate-45 animate-bounce opacity-60"
          style={{ animationDelay: "0.9s", animationDuration: "2.1s" }}
        ></div>
        <div
          className="absolute bottom-1/3 right-1/6 w-40 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent transform -rotate-45 animate-bounce opacity-60"
          style={{ animationDuration: "3.6s" }}
        ></div>

        {/* Círculos con gradientes redistribuidos - movidos desde esquina superior izquierda */}
        <div
          className="absolute top-1/2 right-1/4 w-28 h-28 border-2 border-gradient-to-r from-blue-400 to-purple-400 rounded-full animate-bounce opacity-60"
          style={{ animationDelay: "2.2s", animationDuration: "4.2s" }}
        ></div>
        <div
          className="absolute bottom-1/2 left-1/4 w-36 h-36 border-2 border-gradient-to-r from-pink-400 to-orange-400 rounded-full animate-bounce opacity-60"
          style={{ animationDelay: "0.6s", animationDuration: "2.9s" }}
        ></div>

        {/* NUEVAS FIGURAS REDISTRIBUIDAS */}
        {/* Estrellas animadas - movidas a la esquina superior derecha */}
        <div
          className="absolute top-1/10 right-1/10 w-16 h-16 border-2 border-violet-400 transform rotate-45 animate-bounce opacity-65"
          style={{ animationDelay: "1.3s", animationDuration: "3.1s" }}
        >
          <div className="w-full h-full border-2 border-violet-300 transform rotate-45"></div>
        </div>

        {/* Hexágonos - movidos a la esquina inferior derecha */}
        <div
          className="absolute bottom-1/10 right-1/10 w-20 h-20 border-2 border-emerald-400 transform rotate-30 animate-bounce opacity-55"
          style={{ animationDelay: "0.2s", animationDuration: "2.4s" }}
        >
          <div className="w-full h-full border-2 border-emerald-300 transform rotate-60"></div>
        </div>

        {/* Círculos concéntricos - movidos al centro superior */}
        <div
          className="absolute top-1/6 left-1/2 transform -translate-x-1/2 w-36 h-36 border-2 border-rose-400 rounded-full animate-bounce opacity-60"
          style={{ animationDelay: "1.9s", animationDuration: "3.7s" }}
        >
          <div
            className="w-24 h-24 border-2 border-rose-300 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-bounce"
            style={{ animationDelay: "0.5s", animationDuration: "2.6s" }}
          ></div>
        </div>

        {/* Líneas onduladas - movidas al centro inferior */}
        <div
          className="absolute bottom-1/6 left-1/2 transform -translate-x-1/2 w-44 h-2 bg-gradient-to-r from-transparent via-fuchsia-400 to-transparent transform rotate-12 animate-bounce opacity-50"
          style={{ animationDelay: "0.8s", animationDuration: "3.4s" }}
        ></div>

        {/* Diamantes animados - movidos a la esquina inferior izquierda */}
        <div
          className="absolute bottom-1/8 left-1/8 w-28 h-28 border-2 border-amber-400 transform rotate-45 animate-bounce opacity-65"
          style={{ animationDelay: "2.1s", animationDuration: "2.8s" }}
        ></div>

        {/* Círculos con puntos - movidos al centro derecho */}
        <div
          className="absolute top-1/2 right-1/8 w-32 h-32 border-2 border-sky-400 rounded-full animate-bounce opacity-60"
          style={{ animationDelay: "0.4s", animationDuration: "3.9s" }}
        >
          <div
            className="w-2 h-2 bg-sky-300 rounded-full absolute top-1/4 left-1/4 animate-bounce"
            style={{ animationDelay: "1.1s", animationDuration: "1.8s" }}
          ></div>
          <div
            className="w-2 h-2 bg-sky-300 rounded-full absolute top-3/4 right-1/4 animate-bounce"
            style={{ animationDelay: "1.6s", animationDuration: "2.2s" }}
          ></div>
        </div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 mb-4 shadow-lg">
            {/* Logo geométrico recreado */}
            <div className="w-full h-full relative transform rotate-45 border-2 border-black overflow-hidden">
              {/* Cuadrado interior rotado también como diamante - agrandado para calzar perfectamente */}
              <div className="absolute inset-0 transform rotate-45">
                {/* Triángulo superior izquierdo - rosa claro */}
                <div className="absolute top-0 left-0 w-0 h-0 border-l-[30px] border-l-transparent border-b-[30px] border-b-pink-200"></div>

                {/* Triángulo superior derecho - morado claro */}
                <div className="absolute top-0 right-0 w-0 h-0 border-t-[30px] border-t-transparent border-l-[30px] border-l-purple-200"></div>

                {/* Triángulo inferior izquierdo - celeste */}
                <div className="absolute bottom-0 left-0 w-0 h-0 border-b-[30px] border-b-transparent border-r-[30px] border-r-sky-200"></div>

                {/* Triángulo inferior derecho - amarillo claro */}
                <div className="absolute bottom-0 right-0 w-0 h-0 border-r-[30px] border-r-transparent border-t-[30px] border-t-yellow-200"></div>

                {/* Líneas horizontales y verticales - desde el centro de los lados hacia el centro */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none">
                  <line
                    x1="0"
                    y1="50%"
                    x2="100%"
                    y2="50%"
                    stroke="black"
                    strokeWidth="0.5"
                  />
                  <line
                    x1="50%"
                    y1="0"
                    x2="50%"
                    y2="100%"
                    stroke="black"
                    strokeWidth="0.5"
                  />
                </svg>
              </div>
            </div>
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Fracto
          </h2>
          <p className="mt-2 text-sm text-gray-600">Iniciar Sesión</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                  placeholder="tu@email.com"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="password" className="text-gray-700 font-medium">
                  Contraseña
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                  placeholder="Contraseña"
                  {...register("password")}
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.password.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            </div>

            {errors.root && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                <div className="text-sm text-red-700">
                  {errors.root.message}
                </div>
              </div>
            )}

            <div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200 transform hover:scale-105"
              >
                {isSubmitting ? "Iniciando sesión..." : "Iniciar Sesión"}
              </Button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                ¿No tienes una cuenta?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
                >
                  Regístrate aquí
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
